import React from 'react';
import { strings } from '../../resources/strings';
import { AssemblerManagementOption } from '../../enums/AssemblerManagementOption';
import ServicesContext from '../../contexts/ServicesContext';
import { useAuth } from '../../contexts/AuthContext';
import { SaveOrderNoteInput } from '../../services/OrderNotes/SaveOrderNote/SaveOrderNoteInput';
import { UpdateOrderAssemblyStateInput } from '../../services/Orders/UpdateOrderAssemblyState/UpdateOrderAssemblyStateInput';
import { Recipe } from '../../models/Recipe';
import { AssemblerOrder } from '../../models/AssemblerOrder';
import { CustomerRecipe } from '../../models/CustomerRecipe';
import { TabMenu } from '../../common-components/TabMenu/TabMenu';
import { If } from '../../common-components/If/If';
import { WaitingIcon } from '../../common-components/WaitingIcon/WaitingIcon';
import { Header } from './Header/Header';
import { TabItems } from './TabItems/TabItems';
import { TabRecipes } from './TabRecipes/TabRecipes';
import { TabNotes } from './TabNotes/TabNotes';
import { TabHistoric } from './TabHistoric/TabHistoric';
import { AssemblerOperationForm, IInformedQuantity } from './AssemblerOperationForm/AssemblerOperationForm';
import { AssemblerNoteForm } from './AssemblerNoteForm/AssemblerNoteForm';
import { Container, Content, Tab } from './styles';
import { Mapper } from '../../helpers/Mapper';

const TabOptions = {
  Items: 'tab-items',
  Recipes: 'tab-recipes',
  Notes: 'tab-notes',
  Historic: 'tab-historic'
}

interface IAssemblerOrderDetailsProps {
  orderId?: number;
}

const AssemblerOrderDetails: React.FC<IAssemblerOrderDetailsProps> = ({
  orderId
}) => {

  const { session } = useAuth();

  const [activeTab, setActiveTab] = React.useState<string>(TabOptions.Items);
  const [assemblerOrder, setAssemblerOrder] = React.useState<AssemblerOrder | undefined | null>(null);
  const [recipes, setRecipes] = React.useState<Recipe[]>([]);
  const [customerRecipes, setCustomerRecipes] = React.useState<CustomerRecipe[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [selectedAssemblyOperation, setSelectedAssemblyOperation] = React.useState<AssemblerManagementOption|null|undefined>(null);

  const {
    getAssemblerOrderDetailsByOrderService,
    getOrderRecipesAndLinkedCustomerRecipesService,
    generateTSOReportService,
    updateOrderAssemblyStateService,
    saveOrderNoteService,
    alertService
  } = React.useContext(ServicesContext);

  React.useEffect(() => {
    getOrder();
  }, [orderId]);

  return (
    <>
      <If condition={!selectedAssemblyOperation}>
        <Container>
          <Header
            assemblerOrder={assemblerOrder}
            update={() => getOrder()}
            loading={loading}
            applyAssemblerOperation={applyAssemblerOperation}
          >
            <TabMenu
              tabs={[
                {
                  key: TabOptions.Items,
                  text: strings.Items
                },
                {
                  key: TabOptions.Recipes,
                  text: strings.Recipes
                },
                {
                  key: TabOptions.Notes,
                  text: strings.Notes
                },
                {
                  key: TabOptions.Historic,
                  text: strings.Historic
                }
              ]}
              activeKey={activeTab}
              change={(key) => {
                setActiveTab(key);
              }}
            />
          </Header>
          <Content>
            {loading ? (
              <WaitingIcon />
            ) : (
              <Tab>
                <If condition={activeTab === TabOptions.Items}>
                  <TabItems
                    items={assemblerOrder?.order.orderItems}
                    assemblyHistory={assemblerOrder?.orderLogs}
                  />
                </If>
                <If condition={activeTab === TabOptions.Recipes}>
                  <TabRecipes
                    recipes={recipes}
                    customerRecipes={customerRecipes}
                  />
                </If>
                <If condition={activeTab === TabOptions.Notes}>
                  <TabNotes orderNotes={assemblerOrder?.notes} />
                </If>
                <If condition={activeTab === TabOptions.Historic}>
                  <TabHistoric historic={assemblerOrder?.orderLogs} />
                </If>
              </Tab>
            )}
          </Content>
        </Container>
      </If>
      <If condition={selectedAssemblyOperation !== undefined && 
            selectedAssemblyOperation !== null && 
            selectedAssemblyOperation !== AssemblerManagementOption.AddNote}
      >
        <AssemblerOperationForm
          assemblerManagementOption={selectedAssemblyOperation}
          items={assemblerOrder?.order.orderItems}
          cancel={() => setSelectedAssemblyOperation(null)}
          apply={(informedQuantities) => applyNewInformedQuantities(informedQuantities)}
        />
      </If>
      <If condition={selectedAssemblyOperation === AssemblerManagementOption.AddNote}>
        <AssemblerNoteForm
          cancel={() => setSelectedAssemblyOperation(null)}
          save={(value) => saveNote(value)}
        />
      </If>
    </>
  );

  async function getOrder() {
    if (!orderId) return;

    setLoading(true);

    const results = await Promise.all([
      getAssemblerOrderDetailsByOrderService.handle({ orderId: orderId }),
      getOrderRecipesAndLinkedCustomerRecipesService.handle({ orderId: orderId })
    ]);

    setAssemblerOrder(results[0]?.value);
    setRecipes(results[1]?.value?.recipes || []);

    const customerRecipeList: CustomerRecipe[] = []
    results[1]?.value?.linkedCustomerRecipes?.forEach(linkedCustomerRecipe => {
      if (customerRecipeList.length === 0 || !customerRecipeList.some(crl => crl.id == linkedCustomerRecipe.customerRecipe.id)) {
        customerRecipeList.push(linkedCustomerRecipe.customerRecipe);
      }
    })
    setCustomerRecipes(customerRecipeList);

    setLoading(false);
  }

  function applyAssemblerOperation(operation: AssemblerManagementOption) {
    if (!orderId) return;

    if (operation === AssemblerManagementOption.PrintTSO) {
      generateTSOReportService.handle({orderIds: [orderId]});
      return;
    }

    if (operation === AssemblerManagementOption.AddNote) {
      setActiveTab(TabOptions.Notes);
    }

    setSelectedAssemblyOperation(operation);
  }

  function applyNewInformedQuantities(informedQuantities: IInformedQuantity[]) {
    if (!orderId) return;

    const orderAssemblyStateId = Mapper.getOrderAssemblyStateFromAssemblerManagementOption(selectedAssemblyOperation);
    if (!orderAssemblyStateId) {
      alertService.warning('Operação selecionada não tratada. Informe o time Tech Zerezes.');
      return;
    }

    setLoading(true);
    const input = new UpdateOrderAssemblyStateInput();
    input.orderId = orderId;
    input.userId = session?.user?.id;
    input.orderAssemblyStateId = orderAssemblyStateId;
    input.orderItemList = [...informedQuantities];
    updateOrderAssemblyStateService.handle(input)
      .then(result => {
        if (!result?.value?.result) {
          const message = result?.value?.message || 'Ops! Parece que estamos com algum problema. Se notar algo errado avise o time Tech Zerezes.';
          alertService.warning(message);
        }
        getOrder();
      })
      .catch(error => {
        alertService.warning('Ops! Estou com problemas para aplicar as alterações. Tente novamente e se o problema persistir avise o time Tech Zerezes.');
      })
      .finally(() => {
        setSelectedAssemblyOperation(null);
        setLoading(false);
      });
  }

  function saveNote(value: string) {
    if (!orderId) return;
    setLoading(true);
    const input = new SaveOrderNoteInput();
    input.orderId = orderId;
    input.createdById = session?.user?.id;
    input.value = value;
    saveOrderNoteService.handle(input)
      .then(result => {
        if (!result?.value?.result) {
          const message = 'Ops! Parece que estamos com algum problema. Se notar algo errado avise o time Tech Zerezes.';
          alertService.warning(message);
        }
        getOrder();
      })
      .catch(error => {
        alertService.warning('Ops! Estou com problemas para salvar. Tente novamente e se o problema persistir avise o time Tech Zerezes.');
      })
      .finally(() => {
        setSelectedAssemblyOperation(null);
        setLoading(false);
      });
  }

};

export { AssemblerOrderDetails };